import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, Plus, ShieldCheck, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/EmptyState';
import { useAuthStore } from '@/auth';
import { clinicianHomeHospitalId } from '@/api/clinicianHospital';
import { useHospitalClinicians } from '@/hooks/queries';
import type { ClinicianResponse } from '@/api/clinicians';
import { cn } from '@/lib/utils';
import { AddClinicianDialog } from './AddClinicianDialog';

/**
 * Clinicians management page. Lists every clinician registered in the
 * current clinician's hospital, with a search box and an "Add clinician"
 * button that opens the dialog.
 *
 * Read-only first pass — no edit / deactivate / role management. Those
 * are bigger features (role flags + permission audit) and v1 surfaces
 * them through the same form as Add, so they're a natural followup.
 */
export default function Clinicians() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.clinician);
  const hospitalId = clinicianHomeHospitalId(me) ?? null;
  const cliniciansQuery = useHospitalClinicians(hospitalId);
  const [addOpen, setAddOpen] = useState(false);
  const [q, setQ] = useState('');

  const rows = useMemo<ClinicianResponse[]>(() => {
    const all = cliniciansQuery.data ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((c) => {
      const haystack = [c.first_name, c.last_name, c.email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [cliniciansQuery.data, q]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">{t('Clinicians')}</h1>
        <Button onClick={() => setAddOpen(true)} disabled={!hospitalId}>
          <Plus className="mr-2 h-4 w-4" />
          {t('Add clinician')}
        </Button>
      </div>

      <Card>
        <CardHeader className="space-y-3">
          <CardTitle className="text-base">{t('Hospital roster')}</CardTitle>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('Search by name or email')}
            className="max-w-sm"
          />
        </CardHeader>
        <CardContent>
          <DataTable<ClinicianResponse>
            isLoading={cliniciansQuery.isLoading}
            rows={rows}
            rowKey={(c) => c.id}
            emptyMessage={
              q.trim()
                ? t('No matches.')
                : (
                  <EmptyState
                    title={t('No clinicians yet')}
                    message={t('Add the first clinician to your hospital.')}
                  />
                )
            }
            columns={[
              {
                key: 'name',
                header: t('Name'),
                cell: (c) => {
                  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || '—';
                  const isMe = c.id === me?.id;
                  return (
                    <span className="inline-flex items-center gap-2">
                      <UserCog className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{name}</span>
                      {isMe && (
                        <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide text-primary">
                          {t('You')}
                        </span>
                      )}
                    </span>
                  );
                },
              },
              {
                key: 'email',
                header: t('Email'),
                cell: (c) =>
                  c.email ? (
                    <a
                      href={`mailto:${c.email}`}
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      <Mail className="h-3 w-3 opacity-70" />
                      {c.email}
                    </a>
                  ) : (
                    '—'
                  ),
              },
              {
                key: 'phone',
                header: t('Phone'),
                cell: (c) =>
                  c.phone_number ? (
                    <a
                      href={`tel:${c.phone_number}`}
                      className="inline-flex items-center gap-1.5 tabular-nums text-primary hover:underline"
                    >
                      <Phone className="h-3 w-3 opacity-70" />
                      {c.phone_number}
                    </a>
                  ) : (
                    '—'
                  ),
              },
              {
                key: 'roles',
                header: t('Roles'),
                className: 'w-48',
                cell: (c) => {
                  const badges: { label: string; tone: 'primary' | 'muted' }[] = [];
                  if (c.is_meal_admin) badges.push({ label: t('Meal admin'), tone: 'primary' });
                  if (c.is_report_admin) badges.push({ label: t('Report admin'), tone: 'primary' });
                  if (c.is_internal) badges.push({ label: t('Internal'), tone: 'muted' });
                  if (badges.length === 0) {
                    return <span className="text-xs text-muted-foreground">—</span>;
                  }
                  return (
                    <span className="inline-flex flex-wrap gap-1">
                      {badges.map((b, i) => (
                        <span
                          key={i}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[0.625rem] font-medium',
                            b.tone === 'primary'
                              ? 'bg-primary/10 text-primary'
                              : 'bg-muted text-muted-foreground',
                          )}
                        >
                          <ShieldCheck className="h-2.5 w-2.5" />
                          {b.label}
                        </span>
                      ))}
                    </span>
                  );
                },
              },
            ]}
          />
        </CardContent>
      </Card>

      <AddClinicianDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
